import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { IConversationCardProps } from '@/types/admin/messages';
import { useConversationUtils } from '@/hooks/admin/useMessages';
import { useAppSelector } from '@/store';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';

export const ConversationCard: React.FC<IConversationCardProps> = ({
  conversation,
  onPress,
  isSelected = false,
}) => {
  const {
    truncateSubject,
    truncateMessage,
    isConversationStale,
    getRecipientDisplayName,
  } = useConversationUtils();

  const currentUserId = useAppSelector((state) => state.adminUser.id);
  
  const conversationAccounts = (conversation.accounts || []).filter(
    (acct) => acct.ConversationAccount?.type === 'resident'
  );

  const subjectText = truncateSubject(conversation.subject);
  const lastMessageText = truncateMessage(
    conversation.messages?.[0]?.content || ''
  );
  const recipientName = getRecipientDisplayName(conversation);
  const isStale = isConversationStale(conversation);

  // Get avatar info
  const showGroupIcon = conversationAccounts.length > 1;
  const firstAccount = conversationAccounts[0];

  return (
    <TouchableOpacity
      style={[
        styles.container,
        isSelected && styles.selectedContainer,
      ]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <View style={styles.content}>
        {/* Avatar */}
        <View style={styles.avatarContainer}>
          {showGroupIcon ? (
            <View style={styles.groupAvatar}>
              <SVG_COMPONENTS.GROUP 
                width={16} 
                height={16} 
                color={ADMIN_COLORS.TEXT.PRIMARY}
              />
            </View>
          ) : (
            <View style={styles.userAvatar}>
              <Text style={styles.avatarText}>
                {firstAccount?.firstName?.[0]?.toUpperCase() || 
                 firstAccount?.email?.[0]?.toUpperCase() || '?'}
              </Text>
            </View>
          )}
          
          {/* Stale indicator */}
          {isStale && <View style={styles.staleIndicator} />}
        </View>

        {/* Content */}
        <View style={styles.textContent}>
          <Text style={styles.subject} numberOfLines={1}>
            {subjectText}
          </Text>
          <Text style={styles.lastMessage} numberOfLines={1}>
            {lastMessageText}
          </Text>
          <Text style={styles.recipient} numberOfLines={1}>
            {recipientName}
          </Text>
        </View>

        {/* Selection indicator */}
        {isSelected && (
          <View style={styles.selectionIndicator}>
            <SVG_COMPONENTS.CHECK_CIRCLE 
              width={20} 
              height={20} 
              color={ADMIN_COLORS.PRIMARY}
            />
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    marginHorizontal: 16,
    marginVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    overflow: 'hidden',
  },
  selectedContainer: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.PRIMARY,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  avatarContainer: {
    position: 'relative',
    marginRight: 12,
  },
  userAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    justifyContent: 'center',
    alignItems: 'center',
  },
  groupAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  staleIndicator: {
    position: 'absolute',
    bottom: -2,
    left: -2,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: ADMIN_COLORS.WARNING,
  },
  textContent: {
    flex: 1,
    marginRight: 8,
  },
  subject: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginBottom: 4,
  },
  lastMessage: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginBottom: 4,
  },
  recipient: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
  selectionIndicator: {
    marginLeft: 8,
  },
});
